/**
 * @fileoverview Set Provision — the WIP provisioning modal (MOR-906 §7.4)
 *
 * Purpose: record how much of a matter's gross WIP the firm does not expect
 * to recover, without writing any time off.
 *
 * A provision is not a write-off. Writing off says "we will never bill this"
 * and marks the entries; provisioning says "we do not believe all of this is
 * recoverable yet" and leaves the WIP intact and ageing. The four methods
 * differ only in how the figure is arrived at, and three of them recompute
 * against live gross WIP on every read — so posting or writing off time
 * re-provisions the matter without anyone reopening this dialog.
 *
 * Two methods lean on figures Morgan does not hold yet (a creditor-approved
 * remuneration cap, the funds the estate expects to realise), so they are
 * typed in here. When creditor approvals and realisations are recorded they
 * resolve from those instead (see convex/timesheets/provisions.ts) and this
 * form stops asking.
 *
 * The form is a child component so it only mounts with a real matter id:
 * Convex subscribes a query as soon as its hook mounts, regardless of React
 * Query's `enabled`, so a placeholder id would fail argument validation.
 *
 * @component
 * @example
 * <WipProvisionDialog orgId={orgId} jobId={jobId} open={open} onOpenChange={setOpen} />
 */

import { useState } from "react";
import { format } from "date-fns";
import { useQuery } from "@tanstack/react-query";
import { convexQuery } from "@convex-dev/react-query";
import { useMutation } from "convex/react";
import { Loader2, TriangleAlert } from "lucide-react";
import { toast } from "sonner";

import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import type { WipProvisionMethod } from "@cvx/timesheets/wipProvisions/schema";

import { cn } from "@/utils/misc";
import { extractErrorMessage } from "@/utils/errors";
import { Button } from "@/ui/primitives";
import { Input } from "@/ui/primitives/input";
import { Label } from "@/ui/primitives/label";
import { RadioGroup, RadioGroupItem } from "@/ui/primitives/radio-group";
import { Spinner } from "@/ui/primitives/spinner";
import { Textarea } from "@/ui/primitives/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/ui/composed/overlays/dialog";
import { formatAud } from "./format";

const METHODS: ReadonlyArray<{
  key: WipProvisionMethod;
  label: string;
  hint: string;
}> = [
  {
    key: "fixed_dollar",
    label: "Fixed amount",
    hint: "Hold back a set figure, whatever WIP does.",
  },
  {
    key: "percentage",
    label: "Percentage of WIP",
    hint: "Follows gross WIP up and down on its own.",
  },
  {
    key: "net_approval_cap",
    label: "Net of approved cap",
    hint: "Provide for everything above the creditor-approved remuneration.",
  },
  {
    key: "net_estate_cash",
    label: "Net of estimated funds for realisation",
    hint: "Provide for everything above the expected realisable funds.",
  },
];

/** What the amount field is asking for, per method. */
const AMOUNT_FIELD: Record<
  WipProvisionMethod,
  { label: string; prefix?: string; suffix?: string; missing: string }
> = {
  fixed_dollar: {
    label: "Provision amount",
    prefix: "$",
    missing: "Enter the amount to provide for",
  },
  percentage: {
    label: "Percentage of gross WIP",
    suffix: "%",
    missing: "Enter the percentage to provide for",
  },
  net_approval_cap: {
    label: "Approved remuneration cap",
    prefix: "$",
    missing: "Enter the approved remuneration cap",
  },
  net_estate_cash: {
    label: "Estimated funds available for realisation",
    prefix: "$",
    missing: "Enter the estimated funds available for realisation",
  },
};

/** Blank means "not stated" — distinct from zero, which is a real figure. */
function parseAmount(raw: string): number | undefined {
  const cleaned = raw.replace(/[$,\s]/g, "");
  if (!cleaned) return undefined;
  const value = Number(cleaned);
  return Number.isFinite(value) ? value : undefined;
}

export function WipProvisionDialog({
  orgId,
  jobId,
  open,
  onOpenChange,
}: {
  orgId: Id<"organizations">;
  jobId: Id<"jobs"> | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        {open && jobId ? (
          <ProvisionForm
            orgId={orgId}
            jobId={jobId}
            onClose={() => onOpenChange(false)}
          />
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

function ProvisionForm({
  orgId,
  jobId,
  onClose,
}: {
  orgId: Id<"organizations">;
  jobId: Id<"jobs">;
  onClose: () => void;
}) {
  const { data } = useQuery(
    convexQuery(api.timesheets.provisions.provisionContext, { orgId, jobId }),
  );
  const setProvision = useMutation(api.timesheets.provisions.setProvision);
  const clearProvision = useMutation(api.timesheets.provisions.clearProvision);

  const [draft, setDraft] = useState<{
    method: WipProvisionMethod;
    amount: string;
    notes: string;
  } | null>(null);
  const [busy, setBusy] = useState(false);

  if (data === undefined) {
    return (
      <>
        <DialogHeader>
          <DialogTitle>Set provision</DialogTitle>
          <DialogDescription>Reading the matter's WIP…</DialogDescription>
        </DialogHeader>
        <div className="flex h-48 items-center justify-center">
          <Spinner className="text-muted-foreground" />
        </div>
      </>
    );
  }

  // Seed from the saved provision on first render that has data, so reopening
  // the dialog shows what was applied rather than an empty form.
  const saved = data.provision;
  const current = draft ?? {
    method: saved?.method ?? "percentage",
    amount: String(
      saved?.fixedAmount ??
        saved?.percentageRate ??
        saved?.manualApprovedCap ??
        saved?.manualEstateCash ??
        "",
    ),
    notes: saved?.notes ?? "",
  };

  const gross = data.grossWip;
  const parsed = parseAmount(current.amount);
  const field = AMOUNT_FIELD[current.method];

  /**
   * Preview the figure the way the server will compute it. Deliberately in
   * step with computeProvision in convex/timesheets/provisionMath.ts — a
   * preview that disagrees with the ledger is worse than no preview.
   */
  const preview = ((): { provision: number | null; net: number } => {
    if (parsed === undefined) return { provision: null, net: gross };
    switch (current.method) {
      case "fixed_dollar": {
        const held = Math.min(Math.max(0, parsed), gross);
        return { provision: held, net: gross - held };
      }
      case "percentage": {
        const held = (gross * Math.min(100, Math.max(0, parsed))) / 100;
        return { provision: held, net: gross - held };
      }
      case "net_approval_cap":
      case "net_estate_cash": {
        const ceiling = Math.max(0, parsed);
        return {
          provision: Math.max(0, gross - ceiling),
          net: Math.min(gross, ceiling),
        };
      }
    }
  })();

  const patch = (next: Partial<typeof current>) =>
    setDraft({ ...current, ...next });

  const handleApply = async () => {
    if (parsed === undefined) return;
    setBusy(true);
    try {
      await setProvision({
        orgId,
        jobId,
        method: current.method,
        fixedAmount: current.method === "fixed_dollar" ? parsed : undefined,
        percentageRate: current.method === "percentage" ? parsed : undefined,
        manualApprovedCap:
          current.method === "net_approval_cap" ? parsed : undefined,
        manualEstateCash:
          current.method === "net_estate_cash" ? parsed : undefined,
        notes: current.notes.trim() || undefined,
        periodMonth: format(new Date(), "yyyy-MM"),
      });
      toast.success("Provision applied. Net WIP updated.");
      onClose();
    } catch (err) {
      toast.error(extractErrorMessage(err, "Could not apply the provision."));
    } finally {
      setBusy(false);
    }
  };

  const handleRelease = async () => {
    setBusy(true);
    try {
      await clearProvision({ orgId, jobId });
      toast.success("Provision released. Net WIP is back to gross.");
      onClose();
    } catch (err) {
      toast.error(extractErrorMessage(err, "Could not release the provision."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <DialogHeader>
        <DialogTitle>Set provision — {data.jobName}</DialogTitle>
        <DialogDescription>
          Reduce the WIP you expect to recover without writing any time off. The
          entries stay on the file and keep ageing.
        </DialogDescription>
      </DialogHeader>

      <div className="flex flex-col gap-4">
        <div className="grid grid-cols-3 divide-x divide-border rounded-lg border border-border">
          <Stat label="Gross WIP" value={formatAud(gross)} />
          <Stat
            label="Provision"
            value={
              preview.provision === null
                ? "—"
                : `(${formatAud(preview.provision)})`
            }
            tone={preview.provision ? "warn" : undefined}
          />
          <Stat label="Net WIP" value={formatAud(preview.net)} tone="strong" />
        </div>

        <div className="flex flex-col gap-2">
          <Label>Method</Label>
          <RadioGroup
            value={current.method}
            onValueChange={(next) =>
              // A percentage is not a dollar figure: carrying the number
              // across methods would silently mean something else.
              patch({ method: next as WipProvisionMethod, amount: "" })
            }
            className="gap-1.5"
          >
            {METHODS.map((option) => (
              <label
                key={option.key}
                htmlFor={`provision-${option.key}`}
                className={cn(
                  "flex cursor-pointer items-start gap-2.5 rounded-md border p-2.5 transition-colors",
                  current.method === option.key
                    ? "border-primary bg-primary/5"
                    : "border-border hover:bg-muted/50",
                )}
              >
                <RadioGroupItem
                  id={`provision-${option.key}`}
                  value={option.key}
                  className="mt-0.5"
                />
                <span className="flex min-w-0 flex-col">
                  <span className="text-sm font-medium">{option.label}</span>
                  <span className="text-xs text-muted-foreground">
                    {option.hint}
                  </span>
                </span>
              </label>
            ))}
          </RadioGroup>
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="provision-amount">{field.label}</Label>
          <div className="flex items-center gap-2">
            {field.prefix && (
              <span className="text-sm text-muted-foreground">
                {field.prefix}
              </span>
            )}
            <Input
              id="provision-amount"
              inputMode="decimal"
              value={current.amount}
              onChange={(e) => patch({ amount: e.target.value })}
              placeholder={current.method === "percentage" ? "20" : "0.00"}
              className="w-40"
            />
            {field.suffix && (
              <span className="text-sm text-muted-foreground">
                {field.suffix}
              </span>
            )}
          </div>
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="provision-notes">Reason</Label>
          <Textarea
            id="provision-notes"
            value={current.notes}
            onChange={(e) => patch({ notes: e.target.value })}
            placeholder="e.g. Remuneration approved to $80,000; WIP above that is unlikely to be recovered."
            rows={2}
          />
        </div>

        {saved?.clamped && (
          <p className="flex items-start gap-1.5 text-xs text-amber-600 dark:text-amber-500">
            <TriangleAlert className="mt-0.5 size-3.5 shrink-0" />
            The saved provision is larger than current gross WIP, so it is
            capped at gross. Worth revisiting — time has been written off or
            billed since it was set.
          </p>
        )}

        {data.history.length > 0 && (
          <div className="flex flex-col gap-1 border-t border-border pt-3">
            <p className="text-xs font-medium text-muted-foreground">History</p>
            {data.history.slice(0, 4).map((h) => (
              <p key={h.timestamp} className="text-xs text-muted-foreground">
                {format(new Date(h.timestamp), "d MMM yyyy")} ·{" "}
                {h.actor || "Practitioner"} · {h.reason}
              </p>
            ))}
          </div>
        )}
      </div>

      <DialogFooter className="sm:justify-between">
        {saved ? (
          <Button
            variant="ghost"
            className="text-destructive hover:text-destructive"
            disabled={busy}
            onClick={() => void handleRelease()}
          >
            Release provision
          </Button>
        ) : (
          <span />
        )}
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            disabled={busy || parsed === undefined}
            onClick={() => void handleApply()}
            title={parsed === undefined ? field.missing : undefined}
          >
            {busy && <Loader2 className="size-4 animate-spin" />}
            Apply provision
          </Button>
        </div>
      </DialogFooter>
    </>
  );
}

function Stat({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone?: "warn" | "strong";
}) {
  return (
    <div className="flex flex-col gap-0.5 p-3">
      <span className="text-xs text-muted-foreground">{label}</span>
      <span
        className={cn(
          "text-sm tabular-nums",
          tone === "warn" && "text-amber-600 dark:text-amber-500",
          tone === "strong" && "font-semibold",
        )}
      >
        {value}
      </span>
    </div>
  );
}
